import { useState } from "react";
import { Mail, MapPin, Send } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";

const F = ({ label, textarea, ...p }) => (
  <label className="block">
    <span className="font-mono2 text-[10px] tracking-widest text-ape-muted">{label.toUpperCase()}</span>
    {textarea
      ? <textarea {...p} rows={5} className="w-full bg-transparent border-2 border-ape-border rounded-none p-3 mt-1 text-white focus:border-ape-red focus:outline-none focus:ring-0 transition-colors duration-150" />
      : <input {...p} className="w-full bg-transparent border-b-2 border-ape-border rounded-none py-2 text-white focus:border-ape-red focus:outline-none focus:ring-0 transition-colors duration-150" />}
  </label>
);

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/contact", form);
      toast.success(data.message);
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 grid lg:grid-cols-2 gap-14">
      <div>
        <span className="font-mono2 text-xs tracking-[0.3em] text-ape-red">GET IN TOUCH</span>
        <h1 className="font-display text-5xl sm:text-6xl tracking-tight mt-2">EXCEPTIONAL<br />SUPPORT.</h1>
        <p className="text-ape-muted mt-4 max-w-md">Questions about products, batches, or your order? Our team is committed to exceptional customer support. Reach out — we respond fast.</p>
        <div className="mt-10 space-y-5">
          {[
            { icon: Mail, l: "Support Email", v: "ape-tides.support@ape-tides.com" },
            { icon: MapPin, l: "Based In", v: "United States" },
          ].map((c, i) => (
            <div key={i} className="flex items-center gap-4">
              <div className="border border-ape-border p-3"><c.icon className="text-ape-red" size={22} /></div>
              <div>
                <div className="font-mono2 text-[10px] tracking-widest text-ape-muted">{c.l.toUpperCase()}</div>
                <div className="font-head uppercase tracking-wide">{c.v}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <form onSubmit={submit} className="border border-ape-border bg-ape-surface p-8 space-y-6" data-testid="contact-form">
        <F label="Name" required value={form.name} onChange={set("name")} data-testid="contact-name" />
        <F label="Email" type="email" required value={form.email} onChange={set("email")} data-testid="contact-email" />
        <F label="Subject" required value={form.subject} onChange={set("subject")} data-testid="contact-subject" />
        <F label="Message" textarea required value={form.message} onChange={set("message")} data-testid="contact-message" />
        <button disabled={loading} className="w-full bg-ape-red hover:bg-ape-redhover disabled:opacity-50 text-white font-head uppercase tracking-widest py-4 flex items-center justify-center gap-2 transition-colors duration-150" data-testid="contact-submit">
          {loading ? "Sending..." : <>Send Message <Send size={16} /></>}
        </button>
      </form>
    </div>
  );
}
